import { linkToAquifers, type AquiferLayer } from "./gisLayer.ts";
import { nextId, uniqueName, updateSubBasins, deleteEntitySeriesColumn } from "./entityHelpers.ts";
import { importEntitiesFromGeo, type GisColumnMapping } from "./geoImport.ts";
import type { DailySeries } from "./dailySeries.ts";
import type { Aquifer, EvalHidProject } from "./types.ts";
import { TranslatableError, type Translate } from "../i18n/translate.ts";

export function addAquifer(
  project: EvalHidProject,
  name: string,
  vo: number,
  alfa: number,
  outletCode: number | null
): EvalHidProject {
  const cleanName = name.trim();
  if (!cleanName) throw new TranslatableError("projectEntities.emptyAquiferName");
  if (!(vo > 0)) throw new TranslatableError("projectEntities.invalidVo");
  if (!(alfa > 0)) throw new TranslatableError("projectEntities.invalidAlfa");
  const finalName = uniqueName(
    cleanName,
    project.aquifers.map((a) => a.name)
  );
  const aquifer: Aquifer = {
    aquiferCode: nextId(project.aquifers, (a) => a.aquiferCode),
    name: finalName,
    gisCode: null,
    aquatoolCode: null,
    outletCode,
    vo,
    alfa,
    pumpingCol: 0,
  };
  return { ...project, aquifers: [...project.aquifers, aquifer] };
}

/** Edits the name and parameters of an already-created aquifer. */
export function updateAquifer(
  project: EvalHidProject,
  aquiferCode: number,
  changes: { name: string; vo: number; alfa: number; outletCode: number | null }
): EvalHidProject {
  const cleanName = changes.name.trim();
  if (!cleanName) throw new TranslatableError("projectEntities.emptyAquiferName");
  if (!(changes.vo > 0)) throw new TranslatableError("projectEntities.invalidVo");
  if (!(changes.alfa > 0)) throw new TranslatableError("projectEntities.invalidAlfa");
  const finalName = uniqueName(
    cleanName,
    project.aquifers.filter((a) => a.aquiferCode !== aquiferCode).map((a) => a.name)
  );
  return {
    ...project,
    aquifers: project.aquifers.map((a) =>
      a.aquiferCode === aquiferCode
        ? {
            ...a,
            name: finalName,
            vo: changes.vo,
            alfa: changes.alfa,
            outletCode: changes.outletCode,
          }
        : a
    ),
  };
}

/** Same as `updateSubBasinColumn`, for an aquifer's Pumping index. */
export function updateAquiferColumn(
  project: EvalHidProject,
  aquiferCode: number,
  index: number
): EvalHidProject {
  return {
    ...project,
    aquifers: project.aquifers.map((a) =>
      a.aquiferCode === aquiferCode ? { ...a, pumpingCol: index } : a
    ),
  };
}

/**
 * Permanent delete (like Acuiferos.vb/DG_Acuiferos_UserDeletedRow in the
 * original): removes the aquifer and cascades the unassignment (to 0, the
 * same "no aquifer" sentinel SubBasin already uses) on any sub-basin that
 * had it as its outlet aquifer. No undo.
 */
export function deleteAquifer(project: EvalHidProject, aquiferCode: number): EvalHidProject {
  return {
    ...project,
    aquifers: project.aquifers.filter((a) => a.aquiferCode !== aquiferCode),
    subBasins: project.subBasins.map((c) => ({
      ...c,
      aquiferCode: c.aquiferCode === aquiferCode ? 0 : c.aquiferCode,
    })),
  };
}

/** Aquifer variant of deleteEntitySeriesColumn — pumping (Aquifer.pumpingCol), see PasteDailySeries.tsx's "Delete series". */
export function deleteAquiferPumpingColumn(
  project: EvalHidProject,
  series: DailySeries,
  aquiferCode: number
): { project: EvalHidProject; series: DailySeries | null; sharedWithOthers: boolean } {
  const result = deleteEntitySeriesColumn(
    project.aquifers,
    series,
    (a) => a.aquiferCode === aquiferCode,
    (a) => a.pumpingCol,
    (a, index) => ({ ...a, pumpingCol: index })
  );
  return {
    project: { ...project, aquifers: result.entities },
    series: result.series,
    sharedWithOthers: result.sharedWithOthers,
  };
}

/** Assigns the outlet aquifer to several sub-basins at once (like AsigAcuif.vb). */
export function assignOutletAquiferInBulk(
  project: EvalHidProject,
  subBasinCodes: ReadonlySet<number>,
  aquiferCode: number
): EvalHidProject {
  return updateSubBasins(project, subBasinCodes, (c) => ({ ...c, aquiferCode }));
}

// ---------------------------------------------------------------------------
// The original (GIS.vb) also links aquifers (LayerAcuif, polygons) to their
// own GIS layer, not just sub-basins. This restores that at the data level,
// symmetric to `importAdditionalSubBasins` (subBasinEntities.ts): always
// additive (never replaces what was already there), and only links the row,
// with no area field (unlike a sub-basin, Aquifer has no area of its own —
// Vo/Alfa can't be derived from a shapefile, they're left with editable
// default values afterward). Used both when drawing on the map (App.tsx,
// confirmDrawingName, one feature) and when linking/bulk-creating from an
// uploaded file (LinkGisPanel.tsx, several features at once).
// ---------------------------------------------------------------------------

/** Default Vo/Alfa for an aquifer imported from GIS (no data of its own in the shapefile) — editable afterward with `updateAquifer`. */
const DEFAULT_AQUIFER_VO = 100;
const DEFAULT_AQUIFER_ALFA = 0.1;

export function importAquifersFromGeo(
  project: EvalHidProject,
  geojson: GeoJSON.FeatureCollection,
  mapping: GisColumnMapping,
  t: Translate,
  existingGeometries?: ReadonlySet<string>
): { project: EvalHidProject; newLayer: AquiferLayer } {
  const firstCode = nextId(project.aquifers, (a) => a.aquiferCode);
  const { newEntities: newAquifers, newLayer } = importEntitiesFromGeo(
    geojson,
    existingGeometries,
    project.aquifers,
    (a) => a.gisCode,
    (f, gisCode, i) => {
      const props = f.properties ?? {};
      const finalName = uniqueName(
        String(props[mapping.nameField] ?? t("projectEntities.defaultAquiferName", { n: gisCode })),
        [...project.aquifers.map((a) => a.name)]
      );
      return {
        aquiferCode: firstCode + i,
        name: finalName,
        gisCode,
        aquatoolCode: null,
        outletCode: null,
        vo: DEFAULT_AQUIFER_VO,
        alfa: DEFAULT_AQUIFER_ALFA,
        pumpingCol: 0,
      };
    },
    linkToAquifers
  );

  const aquifers = [...project.aquifers, ...newAquifers];
  return { project: { ...project, aquifers }, newLayer };
}
